import { Injectable, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Filesystem } from '@capacitor/filesystem';
import { AlertController, NavController } from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';

import { AuthCta } from '../../../../common/enums/auth-cta.enum';
import { CommonRoute } from '../../../../common/enums/common-route.enum';
import { ImportMessage } from '../../../../common/enums/import-message.enum';
import { AuthenticationService } from '../../../../common/services/authentication/authentication.service';
import { LogFactory } from '../../../../common/services/logger/log-factory';
import { FitpackManifest } from '../models/sequal.models';
import { FITPACK_EXTENSION, base64ToBytes, unpackFitpack } from './fitpack-container.util';
import { ImportService } from './import.service';

const ALERT_OK = 'OK';
const CANCEL_BUTTON = 'Cancel';
const IMPORT_REVIEW_ROUTE = '/import-review';

/**
 * How long the same document URL is treated as a duplicate delivery rather than a
 * fresh open. Long enough to cover the cold-start case where one tap arrives via
 * both getLaunchUrl() and an appUrlOpen event; far shorter than a user could
 * deliberately re-tap the same file.
 */
export const DUPLICATE_OPEN_WINDOW_MS = 3000;

/**
 * Single entry point for a `.fitpack` that arrives from OUTSIDE the app —
 * tapped in Files / Downloads / an email / a chat, or handed to us by the
 * system share sheet. iOS delivers a `file://…/Inbox/*.fitpack` URL and
 * Android a `content://…` URL through Capacitor's `appUrlOpen` event (and via
 * `getLaunchUrl()` on a cold start); both are registered as openers of the
 * custom document type in ios-changes.js / android-changes.js.
 *
 * The actual "unpack → validate → stage → route to review" step is shared with
 * the in-app file picker (MenuComponent) via {@link openFromBytes}, so a fitpack
 * behaves identically however it enters.
 */
@Injectable({ providedIn: 'root' })
export class FitpackOpenService {
  private readonly importService = inject(ImportService);
  private readonly navCtrl = inject(NavController);
  private readonly alertCtrl = inject(AlertController);
  private readonly auth = inject(AuthenticationService);
  private readonly logger = inject(LogFactory).createLogger(FitpackOpenService);

  private registered = false;
  /**
   * Last document URL handled, with when. Guards the cold-start edge where the
   * same file arrives via BOTH getLaunchUrl() and an appUrlOpen event, which land
   * milliseconds apart — hence a short time window rather than "this URL, for the
   * life of the process". It was the latter, and that silently swallowed every
   * retry: iOS reuses the Inbox filename once {@link cleanupInbox} removes our
   * copy, so tapping the same file again produced an identical URL and nothing
   * happened at all.
   */
  private lastHandled?: { url: string; at: number };

  /**
   * Register the OS "open .fitpack" entry points. Native-only and idempotent —
   * safe to call once at app boot. Also drains the launch URL for the cold-start
   * case where the app was not running when the file was tapped.
   */
  async initialize(): Promise<void> {
    if (this.registered || !Capacitor.isNativePlatform()) return;
    this.registered = true;

    await App.addListener('appUrlOpen', ({ url }) => {
      void this.handleUrl(url);
    });

    try {
      const launch = await App.getLaunchUrl();
      if (launch?.url) await this.handleUrl(launch.url);
    } catch (err) {
      // A missing/unreadable launch URL is not a user-facing failure — the app
      // simply started without a file to open.
      this.logger.debug('No launch URL to open', err);
    }
  }

  /**
   * Unpack, validate and stage a `.fitpack`'s bytes, then route to import review.
   * The one entry point for every way a pack can arrive — the OS file-open
   * handler below and the in-app picker — so the container framing, guest gate,
   * structural check and error copy live in exactly one place.
   *
   * Shows its own alert (and returns `false`) on an unreadable container, the
   * guest block, or a malformed manifest; returns `true` once the manifest is
   * staged and navigation has started.
   */
  async openFromBytes(bytes: Uint8Array): Promise<boolean> {
    let text: string;
    try {
      text = unpackFitpack(bytes);
    } catch (err) {
      // Corrupt gzip: a truncated transfer, or something that was never a pack —
      // Android offers us any unknown binary the user opens (ADR-0097).
      this.logger.error('Failed to unpack .fitpack', err);
      await this.alert(ImportMessage.OpenFailed);
      return false;
    }
    return this.openFromText(text);
  }

  /**
   * Validate + stage a manifest and route to the import-review page. Split from
   * {@link openFromBytes} so the container framing stays separate from what the
   * manifest must contain.
   */
  private async openFromText(text: string): Promise<boolean> {
    // Reference-only .fitpack is a single signed JSON file (no ZIP, no bytes).
    // Parse + structurally validate first so a malformed file is rejected the
    // same way for guests and signed-in users.
    let manifest: FitpackManifest;
    try {
      manifest = JSON.parse(text) as FitpackManifest;
      const payload = manifest.payload as { groups?: unknown[]; activities?: unknown[] };
      if (!payload?.groups || !Array.isArray(payload.activities)) {
        throw new Error(ImportMessage.InvalidStructure);
      }
    } catch (err) {
      this.logger.error('Fitpack import failed', err);
      // Only our own copy reaches the user: a JSON.parse SyntaxError carries a
      // raw engine message ("Unexpected token…"), and with Android offering the
      // app any unknown binary (ADR-0097) that path is routine, not exceptional.
      const message =
        err instanceof Error && err.message === ImportMessage.InvalidStructure
          ? ImportMessage.InvalidStructure
          : ImportMessage.OpenFailed;
      await this.alert(message);
      return false;
    }

    // Stage the pack so it survives an auth round-trip; import-review (and the
    // server media grant) still enforce the signature + Pro checks.
    this.importService.setFitpack({ manifest });

    // A file-open can COLD-START the app, so this can run while the session is
    // still being restored from storage — AuthenticationService starts that in its
    // constructor, but it is async. Reading the token before it lands makes a
    // signed-in user look like a guest: they got the sign-in prompt instead of
    // import review (or nothing at all, when it presented over a still-booting
    // shell). Wait for the same signal the route guards wait for.
    await firstValueFrom(this.auth.whenInitialized());

    // Guests are blocked from importing (ADR-0044) — instead of a dead-end
    // message, offer a one-tap Sign in / Create account and resume import review
    // afterwards.
    if (!this.auth.getAccessToken()) {
      this.logger.debug('Fitpack opened without a session — prompting sign in');
      await this.promptSignInToImport();
      return false;
    }
    await this.navCtrl.navigateForward(IMPORT_REVIEW_ROUTE);
    return true;
  }

  /** Guest tried to import: offer Sign in / Create account (each returning to
   *  import review once authenticated) instead of a dead-end "please sign in". */
  private async promptSignInToImport(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: ImportMessage.AlertHeader,
      message: ImportMessage.SignInToImport,
      buttons: [
        { text: CANCEL_BUTTON, role: 'cancel' },
        { text: AuthCta.CreateAccount, handler: () => void this.goToAuth(CommonRoute.SIGN_UP) },
        { text: AuthCta.SignIn, handler: () => void this.goToAuth(CommonRoute.LOGIN) },
      ],
    });
    await alert.present();
  }

  /** Navigate to login / sign-up, returning to import review once authenticated
   *  (the staged pack is still held by ImportService). */
  private async goToAuth(route: CommonRoute): Promise<void> {
    await this.navCtrl.navigateForward(route, { queryParams: { returnUrl: IMPORT_REVIEW_ROUTE } });
  }

  /**
   * Read a system-delivered document URL and, if it is one of our fitpacks,
   * stage it. Deep links that are NOT document opens (OAuth redirect, payment
   * callback — they use the app's custom URL scheme) are ignored here; those
   * are handled by their own listeners.
   */
  private async handleUrl(url: string): Promise<void> {
    if (!this.isDocumentOpen(url)) return;
    const now = Date.now();
    if (this.lastHandled?.url === url && now - this.lastHandled.at < DUPLICATE_OPEN_WINDOW_MS) {
      this.logger.debug('Ignoring duplicate .fitpack open delivery');
      return;
    }
    this.lastHandled = { url, at: now };

    let bytes: Uint8Array;
    try {
      // No `encoding`: a pack is gzip (ADR-0097) and must be read as bytes.
      // Capacitor returns those base64-encoded on native, and as a Blob on web.
      const { data } = await Filesystem.readFile({ path: url });
      bytes = typeof data === 'string' ? base64ToBytes(data) : new Uint8Array(await data.arrayBuffer());
    } catch (err) {
      this.logger.error('Failed to read opened .fitpack file', err);
      await this.alert(ImportMessage.OpenFailed);
      return;
    }
    await this.openFromBytes(bytes);
    await this.cleanupInbox(url);
  }

  /**
   * True for a system file/document open. `file://` opens (iOS Inbox, Android
   * file managers) must end in `.fitpack`; opaque `content://` opens (Gmail,
   * Drive, chat apps) rarely expose the name, and since ADR-0097 the app is
   * offered any unknown binary on Android, so a content open is treated as a
   * fitpack attempt and rejected on unpack/parse if it isn't one.
   */
  private isDocumentOpen(url: string): boolean {
    const lower = url.toLowerCase();
    if (lower.startsWith('content://')) return true;
    return lower.startsWith('file://') && lower.endsWith(FITPACK_EXTENSION);
  }

  /**
   * iOS copies every opened document into Documents/Inbox; delete our copy once
   * staged so the folder doesn't accumulate. Best-effort — never blocks import.
   */
  private async cleanupInbox(url: string): Promise<void> {
    if (!url.toLowerCase().startsWith('file://') || !url.includes('/Inbox/')) return;
    try {
      await Filesystem.deleteFile({ path: url });
    } catch (err) {
      this.logger.debug('Could not remove imported file from Inbox', err);
    }
  }

  private async alert(message: string): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: ImportMessage.AlertHeader,
      message,
      buttons: [ALERT_OK],
    });
    await alert.present();
  }
}
